<template>
  <div class="demo-units-container">
    <div class="demo-units-header">
      <div>
        <h2>Demo Unit Configuration</h2>
        <span class="mt-4">Max number of demo units available : 10 Units</span>
      </div>
      <div class="demo-units-toggle">
        <v-switch
          v-show="!isLoading && !demoUnitsEnabled"
          v-model="demoUnitsEnabled"
          label="Enable Demo Units"
          :loading="toggleLoading"
          @change="onToggleChange"
        />
      </div>
    </div>

    <v-dialog v-model="removeDialog.show" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">Remove </v-card-title>
        <v-card-text>
          <p>
            Are you sure you want to remove the
            {{ removeDialog.phoneNumber ? "manager phone number" : "" }}
            <strong>{{ removeDialog.phoneNumber }}</strong>
            {{
              removeDialog.deviceId && !removeDialog.phoneNumber ? "device" : ""
            }}
            {{
              removeDialog.phoneNumber && removeDialog.deviceId
                ? "and the device"
                : " "
            }}
            from <strong>{{ removeDialog.unitName }}</strong
            >?
          </p>
          <p class="text-caption text--secondary">
            This will remove
            {{ removeDialog.deviceId ? "the device" : "associated responders"
            }}{{
              removeDialog.phoneNumber && removeDialog.deviceId
                ? "and associated responders"
                : ""
            }}
            from this unit.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelRemove">Cancel</v-btn>
          <v-btn
            color="warning"
            :loading="
              removeDialog.type === 'device'
                ? removeLoadingStates[removeDialog.deviceId] || false
                : responderLoading
            "
            @click="confirmRemove"
          >
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="demo-units-table">
      <v-skeleton-loader
        :loading="isLoading || loading"
        type="table-thead, table-row-divider@6"
      >
        <v-data-table
          :headers="tableHeaders"
          :items="displayItems"
          :loading="false"
          item-key="id"
          no-data-text="No demo units available"
          class="elevation-1"
        >
          <template #item.actions="{ item }">
            <div class="remove-actions">
              <div
                v-if="
                  demoUnitsEnabled &&
                  !item.isMock &&
                  item.devices &&
                  item.devices.length > 0
                "
                class="device-remove-buttons"
              >
                <v-btn
                  v-for="device in item.devices"
                  :key="device.id"
                  depressed
                  small
                  tile
                  color="warning"
                  :loading="removeLoadingStates[device.id] || false"
                  :title="`Remove ${getDeviceDisplayName(device)}`"
                  class="ma-1"
                  @click="onRemoveDevice(device.id, item)"
                >
                  Remove
                </v-btn>
              </div>
              <div v-else class="d-flex align-center">
                <v-btn
                  small
                  color="success"
                  :disabled="
                    !demoUnitsEnabled ||
                    item.isMock ||
                    !getSelectedDeviceForUnit(item.id) ||
                    !hasManagerPhone(item)
                  "
                  :loading="assignLoadingStates[item.id] || false"
                  @click="onAssignDevice(item)"
                  title="Assign Device"
                >
                  <v-icon left small>mdi-plus-circle</v-icon>
                  Assign
                </v-btn>
              </div>
            </div>
          </template>

          <template #item.name="{ item }">
            <span :class="{ 'text--disabled': !demoUnitsEnabled }">
              {{ item.name }}
            </span>
          </template>

          <template #item.device="{ item }">
            <div class="device-selection-container">
              <div
                v-if="item.devices && item.devices.length > 0"
                class="device-chips"
              >
                <span
                  v-for="device in item.devices"
                  :key="device.id"
                  class="ma-1"
                >
                  {{ getDeviceDisplayName(device) }}
                  <span class="ml-1 text-caption"
                    >({{ getDeviceType(device) }})</span
                  >
                </span>
              </div>
              <div v-else>
                <v-autocomplete
                  :value="getSelectedDeviceForUnit(item.id)"
                  :items="deviceSelectItems"
                  item-text="displayText"
                  item-value="id"
                  :placeholder="
                    demoUnitsEnabled && !item.isMock ? 'Select Device' : ''
                  "
                  :disabled="!demoUnitsEnabled || item.isMock"
                  :loading="false"
                  outlined
                  dense
                  clearable
                  hide-details
                  :search-input.sync="deviceSearchText"
                  class="device-dropdown"
                  :filter="customFilter"
                  @input="setSelectedDeviceForUnit(item.id, $event)"
                >
                  <template #item="{ item: deviceItem }">
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        {{ getDeviceDisplayName(deviceItem) }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <span class="text-caption">
                          Type: {{ getDeviceType(deviceItem) }} | Status:
                          {{ getDeviceStatus(deviceItem) }}
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>

                  <template #selection="{ item: deviceItem }">
                    <div class="selected-device-display">
                      <span class="font-weight-medium">{{
                        getDeviceDisplayName(deviceItem)
                      }}</span>
                      <span class="text-caption ml-1"
                        >({{ getDeviceType(deviceItem) }})</span
                      >
                    </div>
                  </template>

                  <template #no-data>
                    <div class="pa-2 text-center">
                      <span v-if="deviceSearchText" class="text-caption">
                        No devices found for "{{ deviceSearchText }}"
                      </span>
                      <span
                        v-else-if="availableDevices.length === 0"
                        class="text-caption"
                      >
                        No available devices
                      </span>
                      <span v-else class="text-caption">
                        Start typing to search devices
                      </span>
                    </div>
                  </template>
                </v-autocomplete>
              </div>
            </div>
          </template>

          <template #item.managerPhone="{ item }">
            <div class="manager-phone-container">
              <div
                v-if="item.managerPhone && !item.isMock"
                class="phone-display"
              >
                <span class="phone-number">{{ item.managerPhone }}</span>
                <v-btn
                  icon
                  small
                  color="error"
                  :loading="isRemovingPhone(item.id)"
                  @click="onRemoveManagerPhone(item)"
                  title="Remove Manager Phone"
                  class="ml-2"
                >
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div v-else class="phone-input-container">
                <InputPhoneNumber
                  v-model="phoneInputs[item.id]"
                  placeholder="Phone Number"
                  label=""
                  :disabled="!demoUnitsEnabled || item.isMock"
                  hide-details
                  dense
                  outlined
                  class="phone-input"
                  @keyup.enter.native="onAddManagerPhone(item)"
                  @input="onPhoneInputChange(item.id, $event)"
                />
                <v-btn
                  small
                  outlined
                  color="primary"
                  :loading="isAddingPhone(item.id)"
                  :disabled="
                    !demoUnitsEnabled ||
                    item.isMock ||
                    !hasValidPhoneValue(item.id)
                  "
                  class="ml-2 phone-add-button"
                  @click="onAddManagerPhone(item)"
                >
                  Add
                </v-btn>
              </div>
            </div>
          </template>

          <template #item.devicesHealth="{ item }">
            <v-list dense color="transparent">
              <v-list-item
                v-for="device in item.devicesHealth || item.devices || []"
                :key="device.id"
                class="ml-0 pl-0"
              >
                <v-list-item-content>
                  <DeviceGaugeCluster :device="device" />
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>

          <template #item.devicesLastMotion="{ item }">
            <v-list dense color="transparent">
              <v-list-item
                v-for="(lastMotion, index) in item.devicesLastMotion || []"
                :key="index"
                class="ml-0 pl-0"
              >
                <v-list-item-content>
                  {{ lastMotion }}
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>
        </v-data-table>
      </v-skeleton-loader>

      <div v-if="demoUnitsEnabled && canAddMore" class="mt-4">
        <v-btn color="primary" :loading="addLoading" @click="onAddMore">
          <v-icon left>mdi-plus</v-icon>
          Add More Demo Unit
        </v-btn>
      </div>
    </div>

    <div
      v-if="demoUnitsEnabled && demoUnits.length > 0"
      class="demo-incidents-section mt-6"
    >
      <div
        class="demo-incidents-header d-flex align-center justify-space-between mb-4"
      >
        <h3>Demo Unit Incidents</h3>
        <DateTimeFilter
          v-model="incidentDateRange"
          :time-zone="facilityTimeZone"
          :ranges="['1m', '2m', '3m']"
          default="1m"
          max-range="6m"
          custom
          inverted
        />
      </div>

      <v-skeleton-loader
        :loading="incidentsLoading"
        type="table-thead, table-row-divider@10"
      >
        <v-data-table
          :headers="incidentHeaders"
          :items="demoIncidents"
          :loading="false"
          item-key="id"
          no-data-text="No incidents for demo units"
          class="elevation-1"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        >
          <template #item.incidentType="{ item }">
            <IconIncidentType :incident="item" class="mr-2" />
            {{ item.incidentType }}
          </template>
          <template #item.startedAt="{ item }">
            {{ facilityFormatDateTime4Humans(item.startedAt) }}
          </template>

          <template #item.duration="{ item }">
            {{ durationInWords(item.startedAt, item.endedAt) }}
          </template>

          <template #item.statusText="{ item }">
            <IconIncidentStatus left small :incident="item" />
          </template>
        </v-data-table>
      </v-skeleton-loader>
    </div>
  </div>
</template>

<script>
import { useDemoUnits } from "./useDemoUnits";
import { formatDeviceName } from "../common/helpers/devices";
import DeviceGaugeCluster from "@atoms/DeviceGaugeCluster.vue";
import InputPhoneNumber from "@tod-ui/components/InputPhoneNumber.vue";
import useFacility from "../authentication/useFacility";
import useAlerts from "@tod-ui/composables/useAlerts";
import { formatPhoneNumber, isValidNumber } from "@tod-ui/helpers/phone";
import { INCIDENT_HISTORY } from "../reports/graphql";
import { incidentStatus, formatIncidentType } from "../common/helpers/incident";
import { diff, durationInWords } from "@tod-ui/helpers/datetime";
import IconIncidentType from "@atoms/IconIncidentType.vue";
import IconIncidentStatus from "@atoms/IconIncidentStatus.vue";
import DateTimeFilter from "@tod-ui/components/DateTimeFilter.vue";

export default {
  name: "DemoUnits",
  components: {
    DeviceGaugeCluster,
    InputPhoneNumber,
    IconIncidentType,
    IconIncidentStatus,
    DateTimeFilter,
  },
  props: {
    facility: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const {
      currentFacilityId,
      facilityTimeZone,
      facilityFormatDateTime4Humans,
      facilityFormatDateTime,
    } = useFacility();
    const composable = useDemoUnits(currentFacilityId.value);
    const { addAlert, clearAllAlerts } = useAlerts();

    return {
      ...composable,
      currentFacilityId,
      facilityTimeZone,
      facilityFormatDateTime4Humans,
      facilityFormatDateTime,
      addAlert,
      clearAllAlerts,
    };
  },
  data() {
    return {
      selectedDevicesForUnits: {},
      phoneInputs: {},
      assignLoadingStates: {},
      responderLoadingStates: {},
      deviceSearchText: "",
      removeLoadingStates: {},
      incidentDateRange: { after: undefined },
      demoIncidents: [],
      incidentsLoading: false,
      removeDialog: {
        show: false,
        unitId: null,
        unitName: "",
        phoneNumber: "",
        type: null,
        deviceId: null,
      },
      tableHeaders: [
        { text: "Actions", value: "actions", sortable: false, width: "10%" },
        { text: "Smart Unit #", value: "name", sortable: true, width: "10%" },
        {
          text: "Device #",
          value: "device",
          sortable: false,
          width: "15%",
        },
        {
          text: "Manager Phone",
          value: "managerPhone",
          sortable: false,
          width: "15%",
        },
        {
          text: "Device Health",
          value: "devicesHealth",
          width: "10%",
          sortable: false,
          class: "non-export",
          cellClass: "non-export",
        },
        {
          text: "Last Motion",
          value: "devicesLastMotion",
          width: "15%",
          class: "text-no-wrap",
        },
      ],
    };
  },
  computed: {
    isLoading() {
      return this.loading || this.$props.loading;
    },
    demoUnitIds() {
      return this.demoUnits.map((unit) => unit.id);
    },
    incidentHeaders() {
      return [
        { text: "Smart Unit #", value: "zoneName", sortable: true },
        { text: "Type", value: "incidentType", sortable: true },
        { text: "Manager Phone", value: "responder", sortable: true },
        { text: "Device #", value: "devices", sortable: true },

        {
          text: "Started At",
          value: "startedAt",
          sortable: true,
          toString: this.facilityFormatDateTime,
        },
        { text: "Duration (min)", value: "duration", sortable: true },
        { text: "Status", value: "statusText", sortable: true },
      ];
    },
    displayItems() {
      if (this.demoUnitsEnabled && this.demoUnits.length > 0) {
        return [...this.demoUnits].sort(
          (a, b) => new Date(a.insertedAt) - new Date(b.insertedAt)
        );
      }
      return this.mockItems;
    },
    canAddMore() {
      return this.demoUnits.length < 10;
    },
    deviceSelectItems() {
      return this.availableDevices.map((device) => ({
        ...device,
        displayText: this.getDeviceDisplayName(device),
      }));
    },
  },

  apollo: {
    demoIncidents: {
      query: INCIDENT_HISTORY,
      variables() {
        return {
          parentId: this.currentFacilityId,
          dateTimeFilter: this.incidentDateRange,
          organizationIds: this.demoUnitIds,
        };
      },
      skip() {
        return !this.incidentDateRange?.after || this.demoUnitIds.length === 0;
      },
      update({ incidents }) {
        this.incidentsLoading = false;
        return incidents.map((incident) => {
          const responder = incident.responders?.length
            ? incident.responders.sort(
                (a, b) => a.escalationLevel - b.escalationLevel
              )[0]
            : null;
          const endedAt =
            incident.lastUntriggeredAt ||
            incident.closedAt ||
            incident.resolvedAt;
          return {
            ...incident,
            zoneName: incident.organization.name,
            incidentType: formatIncidentType(incident),
            devices: incident.devices?.[0]?.shortId || "",
            responder: responder
              ? formatPhoneNumber(responder.contactMethod.value)
              : "",
            endedAt,
            duration: diff(incident.startedAt, endedAt).as("minutes"),
            statusText: incidentStatus(incident),
          };
        });
      },
      loadingKey: "incidentsLoading",
      error() {
        this.incidentsLoading = false;
      },
    },
  },
  watch: {
    incidentDateRange: {
      deep: true,
      handler(newRange, oldRange) {
        if (
          newRange?.after &&
          JSON.stringify(newRange) !== JSON.stringify(oldRange)
        ) {
          this.incidentsLoading = true;
        }
      },
    },
    demoUnitIds: {
      deep: true,
      handler(newIds, oldIds) {
        if (
          JSON.stringify(newIds) !== JSON.stringify(oldIds) &&
          newIds.length > 0
        ) {
          this.incidentsLoading = true;
        }
      },
    },
  },
  mounted() {
    if (this.facility?.id) {
      this.initializeData();
    }
  },
  methods: {
    async initializeData() {
      const apolloClient = this.$apollo.provider.defaultClient;
      await this.fetchDemoUnits(apolloClient);

      if (this.demoUnitsEnabled) {
        await this.fetchInventory(apolloClient);
      }
    },
    hasManagerPhone(item) {
      return item.managerPhone && item.managerPhone.trim() !== "";
    },
    hasPhoneValue(unitId) {
      const value = this.phoneInputs[unitId];
      return value && typeof value === "string" && value.trim().length >= 1;
    },
    hasValidPhoneValue(unitId) {
      const value = this.phoneInputs[unitId];
      return value && isValidNumber(value);
    },
    isAddingPhone(unitId) {
      return Boolean(this.responderLoadingStates[unitId]);
    },
    isRemovingPhone(unitId) {
      return (
        this.removeDialog.type === "manager" &&
        this.removeDialog.unitId === unitId &&
        this.responderLoading
      );
    },
    onPhoneInputChange(unitId, value) {
      this.$set(this.phoneInputs, unitId, value || "");
      this.$forceUpdate();
    },
    getDeviceDisplayName(device) {
      if (!device) return "Unknown Device";

      const formattedName = formatDeviceName(device);
      if (formattedName && formattedName !== "-") return formattedName;

      return (
        device.shortId || device.externalId || device.name || "Unknown Device"
      );
    },
    getDeviceType(device) {
      if (!device) return "Unknown";
      return device.deviceType || device.type || "device";
    },
    getDeviceStatus(device) {
      if (!device) return "Unknown";
      return device.status || "ok";
    },
    getSelectedDeviceForUnit(unitId) {
      return this.selectedDevicesForUnits[unitId] || null;
    },
    setSelectedDeviceForUnit(unitId, deviceId) {
      this.$set(this.selectedDevicesForUnits, unitId, deviceId);
    },
    customFilter(item, queryText) {
      const searchText = queryText?.toLowerCase() || "";
      const deviceName = this.getDeviceDisplayName(item).toLowerCase();
      const deviceType = this.getDeviceType(item).toLowerCase();
      const deviceStatus = this.getDeviceStatus(item).toLowerCase();
      const shortId = (item.shortId || "").toLowerCase();
      const externalId = (item.externalId || "").toLowerCase();

      return (
        deviceName.includes(searchText) ||
        deviceType.includes(searchText) ||
        deviceStatus.includes(searchText) ||
        shortId.includes(searchText) ||
        externalId.includes(searchText)
      );
    },
    async onToggleChange(enabled) {
      await this.executeWithAlert(
        () =>
          this.handleToggleChange(this.$apollo.provider.defaultClient, enabled),
        {
          success: enabled
            ? "Demo units enabled successfully"
            : "Demo units disabled successfully",
          error: "Failed to toggle demo units",
        }
      );
    },
    async onAddMore() {
      await this.executeWithAlert(
        () => this.handleAddMore(this.$apollo.provider.defaultClient),
        {
          success: "Demo unit added successfully",
          error: "Failed to add demo unit",
        }
      );
    },
    async onAssignDevice(unit) {
      const selectedDeviceId = this.getSelectedDeviceForUnit(unit.id);
      if (!selectedDeviceId) return;

      this.$set(this.assignLoadingStates, unit.id, true);

      try {
        await this.executeWithAlert(
          async () => {
            const message = await this.assignDeviceToUnit(
              this.$apollo.provider.defaultClient,
              unit.id,
              selectedDeviceId
            );
            this.setSelectedDeviceForUnit(unit.id, null);
            return message;
          },
          {
            success: null,
            error: "Failed to assign device",
          }
        );
      } finally {
        this.$set(this.assignLoadingStates, unit.id, false);
      }
    },
    async onRemoveDevice(deviceId, unit) {
      if (!deviceId) return;

      this.removeDialog = {
        show: true,
        unitId: unit.id,
        unitName: unit.name,
        phoneNumber: unit.managerPhone,
        type: "device",
        deviceId: deviceId,
      };
    },
    async onAddManagerPhone(item) {
      const phoneNumber = this.phoneInputs[item.id];
      if (!phoneNumber?.trim()) return;

      if (!this.hasValidPhoneValue(item.id)) {
        this.addAlert({
          type: "warning",
          message: "Please enter a valid phone number (minimum 10 digits)",
          timeout: 5,
        });
        return;
      }

      this.$set(this.responderLoadingStates, item.id, true);

      try {
        await this.executeWithAlert(
          async () => {
            const message = await this.addManagerPhone(
              this.$apollo.provider.defaultClient,
              item.id,
              phoneNumber
            );
            this.$set(this.phoneInputs, item.id, "");
            return message;
          },
          {
            success: null,
            error: "Failed to add manager phone",
          }
        );
      } finally {
        this.$set(this.responderLoadingStates, item.id, false);
      }
    },
    async onRemoveManagerPhone(item) {
      this.removeDialog = {
        show: true,
        unitId: item.id,
        unitName: item.name,
        phoneNumber: item.managerPhone,
        type: "manager",
      };
    },
    async confirmRemove() {
      this.removeDialog.show = false;

      try {
        await this.executeWithAlert(
          () =>
            this.removeDialog.type === "device"
              ? this.performDeviceRemoval()
              : this.performManagerRemoval(),
          {
            success: null,
            error: "Failed to remove",
          }
        );
      } finally {
        this.cancelRemove();
      }
    },
    cancelRemove() {
      this.removeDialog = {
        show: false,
        unitId: null,
        unitName: "",
        phoneNumber: "",
        type: null,
        deviceId: null,
      };
    },
    async performDeviceRemoval() {
      const deviceId = this.removeDialog.deviceId;
      this.$set(this.removeLoadingStates, deviceId, true);
      try {
        const message = await this.removeDeviceFromUnit(
          this.$apollo.provider.defaultClient,
          this.currentFacilityId,
          deviceId
        );
        return message || "Device and responders removed successfully";
      } finally {
        this.$set(this.removeLoadingStates, deviceId, false);
      }
    },
    async performManagerRemoval() {
      this.responderLoading = true;
      try {
        const message = await this.removeManagerPhone(
          this.$apollo.provider.defaultClient,
          this.removeDialog.unitId
        );
        return message;
      } finally {
        this.responderLoading = false;
      }
    },
    async executeWithAlert(operation, messages) {
      this.clearAllAlerts();

      try {
        const result = await operation();
        const successMessage = messages.success || result;

        if (successMessage) {
          this.addAlert({
            type: "success",
            message: successMessage,
            timeout: 5,
          });
        }

        return result;
      } catch (error) {
        this.addAlert({
          type: "error",
          message: error.message || error || messages.error,
          timeout: 10,
        });
        throw error;
      }
    },
    durationInWords,
  },
};
</script>

<style scoped>
.demo-units-container {
  padding: 20px;
}

.demo-units-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.demo-units-toggle {
  display: flex;
  align-items: center;
}

.phone-input-container {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
